/**
 * MODULE: _shared/frontend-shell/src/features/crm/utils/contractRenewalLabel.ts
 * PURPOSE: Human-readable renewal proximity labels for contract rows
 * PUBLIC_API: contractRenewalLabel, contractRenewalTone
 */

import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import type { CrmContract } from '@shared/lib/frontend/api/crmContractsApi';

export type ContractRenewalTone = 'neutral' | 'warning' | 'danger' | 'success';

export function contractRenewalLabel(contract: CrmContract): string | null {
  if (!contract.renewalDateISO || contract.status === 'cancelled' || contract.status === 'draft') {
    return null;
  }
  const d = parseISO(contract.renewalDateISO.slice(0, 10));
  if (!isValid(d)) return null;
  const days = differenceInCalendarDays(d, new Date());
  if (contract.status === 'expired' || days < 0) {
    return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  }
  if (days === 0) return 'Renews today';
  if (days === 1) return 'Renews tomorrow';
  if (days <= 30) return `Renews in ${days} days`;
  if (days <= 90) return `Renews in ${days} days`;
  return null;
}

export function contractRenewalTone(contract: CrmContract): ContractRenewalTone {
  const label = contractRenewalLabel(contract);
  if (!label) return 'neutral';
  if (label.startsWith('Expired')) return 'danger';
  if (contract.status === 'active' && label.includes('Renews')) {
    const d = parseISO(contract.renewalDateISO!.slice(0, 10));
    const days = differenceInCalendarDays(d, new Date());
    if (days <= 30) return 'warning';
    if (days <= 90) return 'neutral';
  }
  if (contract.status === 'active') return 'success';
  return 'neutral';
}

export function contractRenewalPillClass(tone: ContractRenewalTone): string {
  switch (tone) {
    case 'danger':
      return 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200';
    case 'warning':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-100';
    case 'success':
      return 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}
